import { useNavigate } from "react-router-dom";
import { Calendar, Sun, Download, Sparkles, XSquare } from "lucide-react";

/* Holiday data */
const HOLIDAYS = [
  { date: "1 Jan", day: "Thursday", name: "New Year", month: "January", quote: "New year, new goals, same desk." },
  { date: "26 Jan", day: "Monday", name: "Republic Day", month: "January", quote: "Celebrating the spirit of of the constitution. 🇮🇳" },
  { date: "4 March", day: "Wednesday", name: "Holi", month: "March", quote: "Colors, sweets, and a mandatory day off!" },
  { date: "15 August", day: "Saturday", name: "Independence Day", month: "August", quote: "Commemorating 79 years of freedom. ⚔️" },
  { date: "28 August", day: "Friday", name: "Rakhi", month: "August", quote: "Celebrating the protector-protected bond." },
  { date: "2 October", day: "Friday", name: "Gandhi Jayanti", month: "October", quote: "Observing the legacy of non-violence." },
  { date: "20 October", day: "Tuesday", name: "Dusshera", month: "October", quote: "Victory of light over darkness." },
  { date: "8 Nov", day: "Sunday", name: "Diwali", month: "November", quote: "Lights, snacks, and deep cleaning." },
  { date: "24 Nov", day: "Tuesday", name: "Guru Nanak Jayanti", month: "November", quote: "Honoring the first Sikh Guru." },
  { date: "25 Dec", day: "Friday", name: "Christmas", month: "December", quote: "Ho-Ho-Home for the holidays! 🎄" },
];

const EMOJIS = {
  "New Year": "🎉",
  "Republic Day": "🇮🇳",
  "Holi": "🎨",
  "Independence Day": "🇮🇳",
  "Rakhi": "🎁",
  "Gandhi Jayanti": "🕊️",
  "Dusshera": "🔥",
  "Diwali": "🪔",
  "Guru Nanak Jayanti": "🙏",
  "Christmas": "🎄",
};

const PAIN_QUOTES = [
  "Weekend overlap? HR is smiling. 💀",
  "A Sunday holiday is no holiday. 😭",
  "Universe 1, Employee 0. 🥲",
  "The system is rigged against us. 🤡",
  "Pain is real, work is eternal. 🫠",
];

const isWeekend = (day) => day === "Saturday" || day === "Sunday";

export default function Holidays() {
  const navigate = useNavigate();

  const grouped = HOLIDAYS.reduce((acc, h) => {
    acc[h.month] = acc[h.month] || [];
    acc[h.month].push(h);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto  md:mt-20  md:mb-20 ">
      <div className="relative overflow-hidden rounded-[24px] md:rounded-[32px] p-4 sm:p-6 md:p-10 bg-gradient-to-br from-[#1e174a] to-[#0f0c29] backdrop-blur-2xl text-white border border-white/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6 mb-8 md:mb-12">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2.5 sm:p-3.5 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600">
              <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2 sm:gap-3">
                Holiday List
                {/* <span className="text-[10px] sm:text-xs font-black bg-white/10 px-2 sm:px-3 py-1 rounded-full text-orange-400 uppercase tracking-[2px]">
                  2026
                </span> */}
              </h1>
              <p className="text-white/50 text-xs sm:text-sm">
                The official calendar for office closures
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <a
              href="/Holidays 2026 1.pdf"
              download
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-[10px] sm:text-xs font-bold"
            >
              <Download className="w-4 h-4 text-blue-400" />
              PDF
            </a>

            <button
              onClick={() => navigate("/")}
              className="flex-1 sm:flex-none py-2 px-3 sm:px-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-600 hover:text-white text-xs"
            >
              Close
            </button>
          </div>
        </div>

        {/* HOLIDAY SECTIONS */}
        <div className="space-y-10 md:space-y-16">
          {Object.entries(grouped).map(([month, list]) => (
            <div key={month}>
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-2xl font-black text-white/40 uppercase tracking-[3px] sm:tracking-[4px]">
                  {month}
                </h2>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {list.map((h, i) => {
                  const weekend = isWeekend(h.day);

                  return (
                    <div
                      key={i}
                      className={`group relative overflow-hidden rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 transition-all duration-500
                        ${
                          weekend
                            ? "bg-gradient-to-br from-red-600/20 to-pink-600/10 border border-red-500/30"
                            : "bg-white/5 border border-white/10 hover:bg-white/[0.08]"
                        }`}
                    >
                      <div className="flex justify-between gap-3 sm:gap-4">
                        <div className="flex gap-3 sm:gap-6 min-w-0">

                          {/* DATE BOX */}
                          <div className={`w-16 h-20 sm:w-18 sm:h-22 md:w-20 md:h-24 rounded-[20px] md:rounded-[28px] flex flex-col ${
                            weekend ? "bg-red-500" : "bg-white/10"
                          }`}>
                            <div className={`h-7 flex items-center justify-center text-[9px] font-black ${
                              weekend ? "bg-red-600" : "bg-white/10"
                            }`}>
                              {h.month.slice(0, 3)}
                            </div>
                            <div className="flex-1 flex flex-col items-center justify-center">
                              <span className="text-lg sm:text-2xl font-black">
                                {h.date.split(" ")[0]}
                              </span>
                              <span className="text-[9px] opacity-60">
                                {h.day.slice(0, 3)}
                              </span>
                            </div>
                          </div>

                          {/* TEXT */}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg sm:text-xl font-black">
                                {h.name}
                              </h3>
                              <span>{EMOJIS[h.name]}</span>
                            </div>

                            <p className="text-xs sm:text-[13px] text-white/50 leading-relaxed">
                              {h.quote}
                            </p>

                            {weekend && (
                              <div className="mt-2 flex items-center gap-2">
                                <XSquare className="w-3.5 h-3.5 text-red-400" />
                                <span className="text-[10px] sm:text-xs text-red-300 italic">
                                  {PAIN_QUOTES[i % PAIN_QUOTES.length]}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ICON */}
                        <div className="flex flex-col items-end gap-1">
                          <div className={`p-2 sm:p-3 rounded-xl ${
                            weekend
                              ? "bg-red-500/20 text-red-300"
                              : "bg-white/10 text-white/40"
                          }`}>
                            {weekend ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="mt-12 md:mt-20 py-6 md:py-10 border-t border-white/5 flex flex-col items-center text-center opacity-40">
          <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400 mb-3 animate-pulse" />
          <p className="text-xs sm:text-sm font-black uppercase tracking-[3px] sm:tracking-[5px]">
            Enjoy your days off
          </p>
        </div>
      </div>
    </div>
  );
}

// import { useNavigate } from "react-router-dom";
// import { Calendar, Sun, Download, Sparkles, Home, MapPin, Share2, XSquare } from "lucide-react";
 
// /* Holiday data */
// const HOLIDAYS = [
//   { date: "1 Jan", day: "Thursday", name: "New Year", month: "January", quote: "New year, new goals, same desk." },
//   { date: "26 Jan", day: "Monday", name: "Republic Day", month: "January", quote: "Celebrating the spirit of of the constitution. 🇮🇳" },
//   { date: "4 March", day: "Wednesday", name: "Holi", month: "March", quote: "Colors, sweets, and a mandatory day off!" },
//   { date: "15 August", day: "Saturday", name: "Independence Day", month: "August", quote: "Commemorating 79 years of freedom. ⚔️" },
//   { date: "28 August", day: "Friday", name: "Rakhi", month: "August", quote: "Celebrating the protector-protected bond." },
//   { date: "2 October", day: "Friday", name: "Gandhi Jayanti", month: "October", quote: "Observing the legacy of non-violence." },
//   { date: "20 October", day: "Tuesday", name: "Dusshera", month: "October", quote: "Victory of light over darkness." },
//   { date: "8 Nov", day: "Sunday", name: "Diwali", month: "November", quote: "Lights, snacks, and deep cleaning." },
//   { date: "24 Nov", day: "Tuesday", name: "Guru Nanak Jayanti", month: "November", quote: "Honoring the first Sikh Guru." },
//   { date: "25 Dec", day: "Friday", name: "Christmas", month: "December", quote: "Ho-Ho-Home for the holidays! 🎄" },
// ];
 
// const EMOJIS = {
//   "New Year": "🎉",
//   "Republic Day": "🇮🇳",
//   "Holi": "🎨",
//   "Independence Day": "🇮🇳",
//   "Rakhi": "🎁",
//   "Gandhi Jayanti": "🕊️",
//   "Dusshera": "🔥",
//   "Diwali": "🪔",
//   "Guru Nanak Jayanti": "🙏",
//   "Christmas": "🎄",
// };
 
// const PAIN_QUOTES = [
//   "Weekend overlap? HR is smiling. 💀",
//   "A Sunday holiday is no holiday. 😭",
//   "Universe 1, Employee 0. 🥲",
//   "The system is rigged against us. 🤡",
//   "Pain is real, work is eternal. 🫠",
// ];
 
// const isWeekend = (day) => day === "Saturday" || day === "Sunday";
 
// export default function Holidays() {
//   const navigate = useNavigate();
 
//   const grouped = HOLIDAYS.reduce((acc, h) => {
//     acc[h.month] = acc[h.month] || [];
//     acc[h.month].push(h);
//     return acc;
//   }, {});
 
//   return (
//     // <div className="max-w-6xl mx-auto md:mt-10 mb-20 px-4">
//     <div className="max-w-8xl mx-auto md:mt-20 mb-20 px-4">

//     <div className="
//             relative overflow-hidden
//             rounded-[32px] p-8 md:p-10
//             bg-gradient-to-br from-[#1e174a] to-[#0f0c29]
//             backdrop-blur-2xl text-white
//             border border-white/10
//             shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)]
//           ">
//       {/* HEADER SECTION */}
//       <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 px-2">
//         <div className="flex items-center gap-4">
//           <div className="p-3.5 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 shadow-lg shadow-orange-500/20">
//             <Sun className="w-5 h-5 text-white" />
//           </div>
//           <div>
//             <h1 className="text-xl font-semibold text-white flex items-center gap-3">
//               Holiday List
//               <span className="text-xs font-black bg-white/10 px-3 py-1 rounded-full text-orange-400 uppercase tracking-[2px]">2026</span>
//             </h1>
//             <p className="text-white/50 text-sm font-medium">The official calendar for office closures</p>
//           </div>
//         </div>
 
//         <div className="flex items-center gap-3">
//           <a
//             href="/Holidays 2026 1.pdf"
//             download
//             className="
//               flex items-center gap-2
//               px-6 py-2 rounded-lg
//               bg-white/5 border border-white/10
//               hover:bg-white/10 transition-all font-bold text-xs 
//             "
//           >
//             <Download className="w-4 h-4 text-blue-400" />
//             OFFICIAL PDF
//           </a>
 
//           <button
//             onClick={() => navigate("/")}
//             className="py-1 px-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-600 hover:text-white transition-all shadow-lg"
//           >
//             close
//             {/* <XSquare className="w-5 h-5" /> */}
//           </button>
//         </div>
//       </div>
 
//       {/* HOLIDAY GRID BY MONTH */}
//       <div className="space-y-16">
//         {Object.entries(grouped).map(([month, list]) => (
//           <div key={month} className="relative">
//             <div className="flex items-center gap-4 mb-6 mt-[-10px]">
//               <h2 className="text-2xl font-black text-white/40 uppercase tracking-[4px] select-none">{month}</h2>
//               <div className="flex-1 h-px bg-white/5" />
//             </div>
 
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
//               {list.map((h, i) => {
//                 const weekend = isWeekend(h.day);
 
//                 return (
//                   <div
//                     key={i}
//                     className={`
//                       group relative overflow-hidden rounded-[40px] p-8
//                       backdrop-blur-2xl border transition-all duration-500
//                       hover:-translate-y-2 hover:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]
//                       ${
//                         weekend
//                           ? "bg-gradient-to-br from-red-600/20 to-pink-600/10 border-red-500/30 shadow-[0_20px_40px_-15px_rgba(239,68,68,0.1)]"
//                           : "bg-white/5 border-white/10 hover:bg-white/[0.08]"
//                       }
//                     `}
//                   >
//                     {/* Animated Glow Background */}
//                     <div className={`absolute top-0 right-0 w-32 h-32 blur-3xl opacity-0 group-hover:opacity-20 transition-all duration-700 ${weekend ? 'bg-red-400' : 'bg-blue-400'}`} />
 
//                     <div className="relative flex items-start justify-between gap-4">
//                       <div className="flex items-start gap-6">
//                         {/* Day / Date Box */}
//                         <div className={`
//                           w-20 h-24 rounded-[28px] overflow-hidden flex flex-col shadow-2xl transform transition duration-500 group-hover:scale-105 group-hover:rotate-3
//                           ${weekend ? 'bg-red-500' : 'bg-white/10'}
//                         `}>
//                           <div className={`h-8 flex items-center justify-center text-[10px] font-black uppercase tracking-widest ${weekend ? 'bg-red-600' : 'bg-white/10'}`}>
//                             {h.month.slice(0, 3)}
//                           </div>
//                           <div className="flex-1 flex flex-col items-center justify-center">
//                             <span className="text-2xl font-black">{h.date.split(" ")[0]}</span>
//                             <span className="text-[10px] font-bold opacity-60 uppercase">{h.day.slice(0, 3)}</span>
//                           </div>
//                         </div>
 
//                         {/* Text Content */}
//                         <div>
//                           <div className="flex items-center gap-2 mb-2">
//                             <h3 className="text-xl font-black text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-white/60">
//                               {h.name}
//                             </h3>
//                             <span className="text-xl group-hover:animate-bounce transition">{EMOJIS[h.name] || "✨"}</span>
//                           </div>
 
//                           <p className="text-[13px] text-white/50 font-medium leading-relaxed max-w-[200px]">
//                             {h.quote}
//                           </p>
 
//                           {weekend && (
//                             <div className="mt-4 flex items-center gap-2">
//                               <XSquare className="w-3.5 h-3.5 text-red-400" />
//                               <span className="text-xs font-black text-red-300 italic uppercase">
//                                 {PAIN_QUOTES[i % PAIN_QUOTES.length]}
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
 
//                       {/* Action Info */}
//                       <div className="flex flex-col items-end gap-1">
//                         <div className={`p-3 rounded-2xl transform transition duration-500 group-hover:-rotate-12 ${weekend ? 'bg-red-500/20 text-red-300' : 'bg-white/10 text-white/40 group-hover:text-white'}`}>
//                           {weekend ? <Sun className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
//                         </div>
//                         <span className="text-[9px] font-black text-white/10 mt-2 uppercase vertical-text tracking-[2px]">CLOSED</span>
//                       </div>
//                     </div>
//                   </div>
//                 );
//               })}
//             </div>
//           </div>
//         ))}
//       </div>
 
//       {/* FOOTER ACCENT */}
//       <div className="mt-20 py-10 border-t border-white/5 flex flex-col items-center text-center opacity-30">
//         <Sparkles className="w-8 h-8 text-blue-400 mb-4 animate-pulse" />
//         <p className="text-sm font-black uppercase tracking-[5px]">Enjoy your days off</p>
//       </div>
//     </div>
//     </div>
//   );
// }
 






// import { useNavigate } from "react-router-dom";
// import { Calendar, Sun } from "lucide-react";
// import {  Download } from "lucide-react";
// /* Holiday data */
// const HOLIDAYS = [
//   { date: "1 Jan", day: "Thursday", name: "New Year", month: "January", quote: "New year, new excuses to take leave." },
//   { date: "26 Jan", day: "Monday", name: "Republic Day", month: "January", quote: "Celebrating the day India became a republic 🇮🇳" },
//   { date: "4 March", day: "Wednesday", name: "Holi", month: "March", quote: "Festival of colors and unlimited gulaal." },
//   { date: "15 August", day: "Saturday", name: "Independence Day", month: "August", quote: "Freedom from British rule (but not from office)." },
//   { date: "28 August", day: "Friday", name: "Rakhi", month: "August", quote: "Celebrating the bond of siblings." },
//   { date: "2 October", day: "Friday", name: "Gandhi Jayanti", month: "October", quote: "Birth anniversary of Mahatma Gandhi." },
//   { date: "20 October", day: "Tuesday", name: "Dusshera", month: "October", quote: "Victory of good over evil." },
//   { date: "8 Nov", day: "Sunday", name: "Diwali", month: "November", quote: "Festival of lights and empty wallets." },
//   { date: "24 Nov", day: "Tuesday", name: "Guru Nanak Jayanti", month: "November", quote: "Birth anniversary of Guru Nanak Dev Ji." },
//   { date: "25 Dec", day: "Friday", name: "Christmas", month: "December", quote: "Celebrating the birth of Jesus Christ 🎄" },
// ];

// /* Emojis per holiday */
// const EMOJIS = {
//   "New Year": "🎉",
//   "Republic Day": "🇮🇳",
//   "Holi": "🎨",
//   "Independence Day": "🇮🇳",
//   "Rakhi": "🎁",
//   "Gandhi Jayanti": "🕊️",
//   "Dusshera": "🔥",
//   "Diwali": "🪔",
//   "Guru Nanak Jayanti": "🙏",
//   "Christmas": "🎄",
// };

// const PAIN_QUOTES = [
//   "No extra off 💀 , Pain is real 😭",
//   "Wasted holiday 😭",
//   "Office wins again 🥲",
//   "No extra off 💀 , Pain is real 😭",
//   "Pain is real 🫠",
//   "HR moment 🤡",
//   "System rigged 😤",
// ];

// const isWeekend = (day) =>
//   day === "Saturday" || day === "Sunday";

// export default function Holidays() {
//   const navigate = useNavigate();

//   const grouped = HOLIDAYS.reduce((acc, h) => {
//     acc[h.month] = acc[h.month] || [];
//     acc[h.month].push(h);
//     return acc;
//   }, {});

//   return (
//     <div className="
//       rounded-2xl p-8 md:mt-20
//       bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f]
//       text-white shadow-2xl
//     ">
    



// <div className="flex md:flex-row flex-col justify-between items-center mb-8">
//   <h2 className="text-2xl font-bold">
//     Holidays · 2026
//   </h2>

//   <div className="flex items-center gap-3 mt-2 md:mt-0">
//     <a
//       href="/Holidays 2026 1.pdf"   // put your actual pdf file here
//       download
//       className="
//         flex items-center gap-2
//         px-4 py-2 rounded-lg
//         bg-blue-500/20 text-blue-300
//         hover:bg-blue-500/40
//         transition text-sm
//       "
//     >
//       <Download size={16} />
//       Download PDF
//     </a>

//     <button
//       onClick={() => navigate("/")}
//       className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/40 transition text-sm"
//     >
//       Close
//     </button>
//   </div>
// </div>









//       {Object.entries(grouped).map(([month, list]) => (
//         <div key={month} className="mb-12">
//           <h3 className="text-lg font-semibold mb-4 text-white/70">
//             {month}
//           </h3>

//           <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
//             {list.map((h, i) => {
//               const weekend = isWeekend(h.day);

//               return (
//                 <div
//                   key={i}
//                   className={`
//                     group relative overflow-hidden
//                     rounded-2xl p-5
//                     border backdrop-blur-xl
//                     animate-[float_6s_ease-in-out_infinite]
//                     transition-all duration-300
//                     hover:-translate-y-1 hover:scale-[1.02]
//                     hover:shadow-[0_20px_50px_rgba(0,0,0,0.5)]
//                     ${
//                       weekend
//                         ? "bg-gradient-to-br from-pink-500/30 to-red-500/20 border-pink-400/40 hover:animate-[wiggle_0.3s_ease-in-out]"
//                         : "bg-white/10 border-white/20 hover:bg-white/20"
//                     }
//                   `}
//                 >
//                   {/* Sparkle */}
//                   <div className="
//                     absolute top-2 right-2
//                     opacity-0 group-hover:opacity-100
//                     transition text-yellow-300
//                   ">
//                     ✨
//                   </div>

//                   <div className="relative flex items-center justify-between">
//                     <div className="flex items-center gap-4">
//                       <div className="
//                         w-14 h-14 rounded-xl
//                         bg-black/30 flex flex-col items-center justify-center
//                         text-sm font-semibold
//                       ">
//                         <span>{h.date.split(" ")[0]}</span>
//                         <span className="text-xs opacity-70">
//                           {h.date.split(" ")[1]}
//                         </span>
//                       </div>

//                       <div>
//                         <p className="text-sm opacity-70">
//                           {h.day}
//                         </p>
//                         <h3 className="text-lg font-semibold">
//                           {h.name}
//                           <span className="ml-2">
//                             {EMOJIS[h.name] || "✨"}
//                           </span>
//                         </h3>

//                         <p className="text-sm text-white/70 mt-1">
//                           {h.quote}
//                         </p>

//                         <p className="text-xs text-white/50 italic">
//                           Tap to feel the vibe 😌
//                         </p>

//                         {weekend && (
//                           <div className="mt-2 text-sm text-red-200 italic">
//                             {PAIN_QUOTES[i % PAIN_QUOTES.length]}
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     <div className="
//                       text-blue-300
//                       group-hover:rotate-6 transition
//                     ">
//                       {weekend ? (
//                         <Sun size={24} />
//                       ) : (
//                         <Calendar size={24} />
//                       )}
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         </div>
//       ))}
//     </div>
//   );
// }
